import { computed, ref, Ref, watch, WatchStopHandle } from 'vue';
import { ComplexModal } from '@studyportals/modal';
import { FiltersDisplayedEvent } from '@studyportals/search-filters';
import store from '@/study-search/store';
import { ResultsLabel } from '@/study-search/models/search-summary/ResultsLabel';
import { Filter, FilterType } from '@/study-search/models/filter';
import { IEventAggregationService } from '@studyportals/event-aggregation-service-interface';
import { EventAggregationService } from '@/search-page/platform/EventAggregationService';
import { ApplicationStatusOptions } from '@/study-search/models/application-status/ApplicationStatusOptions';
import getters from "@/study-search/store/getters";
import { PortalType } from '@studyportals/domain-client';

export default class SearchBar {
	public modal: ComplexModal | undefined;
	public modalContent = ref();
	public modalShowContent = ref(false);

	private ignoredFilterTypes: string[] = [
		FilterType.AREA,
		FilterType.KEYWORD_WHAT,
		FilterType.KEYWORD_WHERE,
		FilterType.ORGANISATIONS,
		FilterType.EDUCATION_LEVEL
	];

	private showFilters: Ref<boolean> = ref(true);
	private readonly eventAggregationService: IEventAggregationService;

	private boundShowOrHide: () => void;
	public readonly filtersBarMobile = ref<HTMLElement | null>(null);
	public readonly floatingFiltersButtonMobileWrapper = ref<HTMLElement | null>(null);
	private upperThreshold = 0;
	private lowerThreshold = false;
	private unwatchFloatingButton: WatchStopHandle | null = null;
	private static readonly IS_IN_VIEWPORT: string = 'is-inViewport';

	public constructor(eventAggregationService?: IEventAggregationService) {
		this.eventAggregationService = eventAggregationService ?? EventAggregationService.instance;
		this.boundShowOrHide = this.showOrHide.bind(this);
	}

	public shouldShowFilters = computed({
		get: () => this.showFilters.value,
		set: (val) => {
			this.showFilters.value = val;
		}
	});

	public filterHintCount = computed((): number => {
		const filters: Filter[] = store.getters.getFilters();
		return filters.filter((_) => !this.ignoredFilterTypes.includes(_.type) && _.values.length > 0).length;
	});

	public totalSearchResults = computed(() => {
		return store.getters.getTotalSearchResults();
	});

	public isApplicationStatusNotReady = computed((): boolean => {
		return store.getters.getApplicationStatus() as ApplicationStatusOptions !== ApplicationStatusOptions.READY;
	});

	public currentPageNumber = computed(() => {
		return store.getters.getPageNumber();
	});

	public resultsLabel = computed((): string => {
		const portalType: PortalType = store.getters.getPortalType();
		switch (portalType) {
			case PortalType.MASTER:
				return ResultsLabel.MASTERS;
			case PortalType.BACHELOR:
				return ResultsLabel.BACHELORS;
			case PortalType.SHORT:
				return ResultsLabel.SHORT_COURSES;
			case PortalType.PHD:
				return ResultsLabel.PHDS;

			default:
				return ResultsLabel.DEFAULT;
		}
	});

	public showFiltersInModal(): void {
		this.showFilters.value = true;
	}

	public showSortInModal(): void {
		this.showFilters.value = false;
	}

	public closeModal(): void {
		if (this.modal) {
			window.ModalManager.close(this.modal);
			this.modalShowContent.value = false;
		}
	}

	public openModal(section: string): void {
		this.shouldShowFilters.value = section !== 'sort';
		this.modalShowContent.value = true;
		if (this.modal) {
			window.ModalManager.open(this.modal);
			this.eventAggregationService.publishTo(FiltersDisplayedEvent.EventType, new FiltersDisplayedEvent());
		}
	}

	public mounted(): void {
		this.initiatePopup();
		this.initiateFloatingButton();
		this.unwatchFloatingButton = watch(this.applicationStatus, this.initiateFloatingButton.bind(this));
	}

	public unmounted(): void {
		this.closeModal();
		this.removeEventListeners();
	}

	public removeEventListeners(): void {
		window.removeEventListener('load', this.boundShowOrHide);
		window.removeEventListener('scroll', this.boundShowOrHide);
		window.removeEventListener('resize', this.boundShowOrHide);
	}

	public initiatePopup(): void {
		if (!this.modalContent.value) {
			return;
		}

		this.modal = new ComplexModal(
			(this.modalContent.value as HTMLElement),
			{
				cssClassName: 'FilterMobileWrapper',
				destroyOnClose: true,
				onClose: this.checkStateOfFloatingButton.bind(this)
			}
		);
	}

	public initiateFloatingButton(): void {
		if (!this.applicationStatusReady.value) {
			return;
		}
		this.calculateUpperThreshold();
		this.showOrHide();

		window.addEventListener('load', this.boundShowOrHide);
		window.addEventListener('scroll', this.boundShowOrHide);
		window.addEventListener('resize', this.boundShowOrHide);

		if (this.unwatchFloatingButton !== null) {
			this.unwatchFloatingButton();
		}
	}

	public checkStateOfFloatingButton(): void {
		if (!this.applicationStatusReady.value || this.noSearchResults.value) {
			this.floatingFiltersButtonMobileWrapper.value?.classList.remove(SearchBar.IS_IN_VIEWPORT);
		}

		this.calculateUpperThreshold();
		this.showOrHide();
	}

	public calculateUpperThreshold(): void {
		if (this.filtersBarMobile.value) {
			this.upperThreshold = this.filtersBarMobile.value.offsetTop + this.filtersBarMobile.value.offsetHeight;
		}
	}

	public showOrHide(): void {
		const searchContainer = document.querySelector('.js-searchContainer');
		if (!searchContainer) {
			return;
		}

		const rect = searchContainer.getBoundingClientRect();
		const windowHeight = (window.innerHeight || document.documentElement.clientHeight);
		this.lowerThreshold = rect.bottom <= windowHeight;

		if (window.scrollY > this.upperThreshold && !this.lowerThreshold) {
			this.floatingFiltersButtonMobileWrapper.value?.classList.add(SearchBar.IS_IN_VIEWPORT);
		} else {
			this.floatingFiltersButtonMobileWrapper.value?.classList.remove(SearchBar.IS_IN_VIEWPORT);
		}
	}

	public noSearchResults = computed((): boolean => {
		return getters.getTotalSearchResults() === 0;
	});

	public applicationStatusReady = computed((): boolean => {
		return getters.getApplicationStatus() as ApplicationStatusOptions === ApplicationStatusOptions.READY;
	});

	public applicationStatus = computed((): string => {
		return getters.getApplicationStatus();
	});
}
